import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API_URL } from '../config';

function DirectionBadge({ direction, t }) {
  const config = {
    rising: { label: t('trends.directionRising'), className: 'bg-[#0f2a0f] text-accent border-[#166534]', icon: '\u2191' },
    falling: { label: t('trends.directionFalling'), className: 'bg-[#2a0f0f] text-red-400 border-[#7f1d1d]', icon: '\u2193' },
    stable: { label: t('trends.directionStable'), className: 'bg-surfaceAlt text-muted border-edge', icon: '\u2192' },
  };
  const { label, className, icon } = config[direction] || config.stable;
  return (
    <span className={`inline-flex items-center gap-1 border px-3 py-1 rounded-full text-xs font-semibold ${className}`}>
      {icon} {label}
    </span>
  );
}

function Trends({ token, onAuthError }) {
  const { t } = useTranslation();
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState('');
  const [catalogNote, setCatalogNote] = useState('');
  // 429 = tope diario de consultas: se muestra el mensaje del servidor (ya traducido)
  const limitMessage = (err, fallback) => {
    const detail = err.response && err.response.data && err.response.data.detail;
    return err.response && err.response.status === 429 && typeof detail === 'string' ? detail : fallback;
  };
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [period, setPeriod] = useState('7d');
  const [related, setRelated] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [relatedError, setRelatedError] = useState('');
  const [category, setCategory] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState('');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchLatestTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLatestTrends = async () => {
    setLoadingLatest(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/trends/latest`, authHeaders);
      const data = response.data.data || [];
      setTrends(data);
      if (data.length > 0) setSelectedProductId(data[0].product_id);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
    } finally {
      setLoadingLatest(false);
    }
  };

  const fetchTrends = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/v1/trends`, { ...authHeaders, params: { period } });
      const data = response.data.data || [];
      const notes = [];
      if (response.data.total_products > response.data.analyzed) {
        notes.push(t('trends.catalogTop', { count: response.data.analyzed, total: response.data.total_products }));
      }
      if (response.data.limited) notes.push(t('trends.catalogLimited'));
      setCatalogNote(notes.join(' '));
      setTrends(data);
      setHasFetched(true);
      if (data.length > 0 && !selectedProductId) setSelectedProductId(data[0].product_id);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onAuthError();
      } else {
        setError(limitMessage(err, t('trends.loadError')));
      }
    } finally {
      setLoading(false);
    }
  };

  const searchTrend = async (e, termOverride) => {
    if (e) e.preventDefault();
    const term = (termOverride || searchTerm).trim();
    if (!term) return;
    setRelated(null);
    setRelatedError('');
    setSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const response = await axios.get(`${API_URL}/api/v1/trends/search`, {
        ...authHeaders,
        params: { q: term, period },
      });
      const data = response.data.data;
      if (!data) {
        setSearchError(t('trends.searchNotFound'));
      } else {
        setSearchResult(data);
        setSelectedProductId(null);
      }
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setSearchError(limitMessage(err, t('trends.loadError')));
    } finally {
      setSearching(false);
    }
  };

  const fetchRelated = async (term) => {
    setLoadingRelated(true);
    setRelatedError('');
    setRelated(null);
    try {
      const response = await axios.get(`${API_URL}/api/v1/trends/related`, { ...authHeaders, params: { q: term, period } });
      setRelated({ term, ...response.data.data });
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setRelatedError(limitMessage(err, t('trends.relatedError')));
    } finally {
      setLoadingRelated(false);
    }
  };

  const searchRelated = (query) => {
    setSearchTerm(query);
    searchTrend(null, query);
  };

  const periods = ['7d', '30d', '90d', '12m'];
  const categories = ['electronics', 'fashion', 'beauty', 'jewelry', 'home', 'garden', 'sports', 'health',
    'baby', 'toys', 'videogames', 'books', 'motor', 'pets', 'food'];

  const fetchCategory = async (cat, seed) => {
    setCategory(cat);
    setLoadingCategory(true);
    setCategoryError('');
    setCategoryData(null);
    try {
      const response = await axios.get(`${API_URL}/api/v1/trends/category`, { ...authHeaders, params: { category: cat, period, seed: seed || '' } });
      setCategoryData(response.data.data);
    } catch (err) {
      if (err.response && err.response.status === 401) onAuthError();
      else setCategoryError(limitMessage(err, t('trends.categoryError')));
    } finally {
      setLoadingCategory(false);
    }
  };

  const selectedTrend = trends.find((tr) => tr.product_id === selectedProductId);
  const chartData = searchResult || selectedTrend;

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h2 className="text-white text-2xl font-bold">{t('trends.title')}</h2>
          <p className="text-muted text-sm mt-1">{t('trends.subtitle')}</p>
          <div className="flex flex-wrap items-center gap-1 mt-3">
            <span className="text-muted text-xs mr-1">{t('trends.periodLabel')}</span>
            {periods.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setPeriod(p); setSearchResult(null); setRelated(null); }}
                className={`text-xs px-3 py-1 rounded-full border cursor-pointer ${period === p ? 'bg-accent text-panel border-accent font-semibold' : 'bg-surfaceAlt text-muted border-edge'}`}
              >
                {t(`trends.period_${p}`)}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={fetchTrends}
          disabled={loading}
          className={`self-start sm:self-auto bg-accent text-panel font-bold text-sm px-5 py-2.5 rounded-lg cursor-pointer ${loading ? 'opacity-70' : ''}`}
        >
          {loading ? t('trends.checking') : t('trends.refresh')}
        </button>
      </div>

      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-4 mb-6">
          {error}
        </div>
      )}

      {catalogNote && (
        <div className="bg-surface border border-amber-500/40 text-amber-400 text-sm rounded-lg p-3 mb-6">
          {catalogNote}
        </div>
      )}

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
        <form onSubmit={searchTrend} className="flex flex-col sm:flex-row gap-3 mb-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('trends.searchPlaceholder')}
            className="flex-1 bg-surfaceAlt border border-edge rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-accent"
          />
          <button
            type="submit"
            disabled={searching}
            className={`bg-surfaceAlt border border-edge text-white font-semibold text-sm px-5 py-2 rounded-lg cursor-pointer ${searching ? 'opacity-70' : ''}`}
          >
            {searching ? t('trends.searching') : t('trends.searchButton')}
          </button>
        </form>
        <p className="text-muted text-[11px] mb-6">{t('trends.searchCostNote')}</p>

        {searchError && (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
            {searchError}
          </div>
        )}

        {searchResult && (
          <div
            onClick={() => { setSearchResult(null); }}
            className="bg-surfaceAlt border border-accent rounded-xl p-5 mb-6 cursor-pointer max-w-xs"
            title={t('trends.searchButton')}
          >
            <p className="text-white text-sm font-semibold mb-3">{searchResult.product_name}</p>
            <div className="flex items-center justify-between">
              <span className="text-muted text-xs">{t('trends.volume')}: {searchResult.search_volume}</span>
              <DirectionBadge direction={searchResult.trend_direction} t={t} />
            </div>
            <p className="text-muted text-[11px] mt-2">{t(`trends.period_${searchResult.period || '7d'}`)}{typeof searchResult.change_pct === 'number' ? ` \u00b7 ${searchResult.change_pct > 0 ? '+' : ''}${searchResult.change_pct} % ${searchResult.comparison === 'yoy' ? t('trends.vsLastYear') : t('trends.vsStartOfPeriod')}` : ''}</p>
          </div>
        )}

        {searchResult && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => fetchRelated(searchResult.product_name)}
              disabled={loadingRelated}
              className={`bg-surfaceAlt border border-edge text-white text-sm px-4 py-2 rounded-lg cursor-pointer ${loadingRelated ? 'opacity-70' : ''}`}
            >
              {loadingRelated ? t('trends.relatedLoading') : t('trends.relatedButton')}
            </button>
            {relatedError && <p className="text-red-400 text-xs mt-2">{relatedError}</p>}
            {related && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {['rising', 'top'].map((kind) => (
                  <div key={kind} className="bg-surfaceAlt border border-edge rounded-xl p-4">
                    <p className="text-white text-sm font-semibold mb-1">{t(`trends.related_${kind}`)}</p>
                    <p className="text-muted text-[11px] mb-3">{t(`trends.related_${kind}Hint`)}</p>
                    {(related[kind] || []).length === 0 ? (
                      <p className="text-muted text-xs">{t('trends.relatedEmpty')}</p>
                    ) : (
                      related[kind].map((r) => (
                        <button
                          key={r.query}
                          type="button"
                          onClick={() => searchRelated(r.query)}
                          className="w-full flex items-center justify-between gap-3 py-1.5 text-sm border-b border-edge last:border-0 bg-transparent cursor-pointer text-left"
                          title={t('trends.relatedClick')}
                        >
                          <span className="text-white truncate">{r.query}</span>
                          <span className={`whitespace-nowrap text-xs ${kind === 'rising' ? 'text-accent' : 'text-muted'}`}>
                            {r.breakout ? t('trends.breakout') : (kind === 'rising' ? r.value : r.extracted_value)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
        <h3 className="text-white text-base font-semibold mb-1">{t('trends.categoryTitle')}</h3>
        <p className="text-muted text-xs mb-4">{t('trends.categorySubtitle')}</p>
        <div className="flex flex-wrap gap-2 mb-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              disabled={loadingCategory}
              onClick={() => fetchCategory(c)}
              className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer ${category === c ? 'bg-accent text-panel border-accent font-semibold' : 'bg-surfaceAlt text-white border-edge'}`}
            >
              {t(`trends.cat_${c}`)}
            </button>
          ))}
        </div>
        <p className="text-muted text-[11px]">{t('trends.categoryNote')}</p>
        {loadingCategory && <p className="text-muted text-sm mt-4">{t('trends.relatedLoading')}</p>}
        {categoryError && <p className="text-red-400 text-xs mt-3">{categoryError}</p>}
        {categoryData && !loadingCategory && (
          <>
            <p className="text-muted text-[11px] mt-3">
              {t(`trends.cat_${categoryData.category}`)} {'\u00b7'} {t(`trends.period_${categoryData.period}`)} {'\u00b7'} {categoryData.geo}
              {categoryData.source === 'seed' && categoryData.seed ? ` \u00b7 ${t('trends.categorySeed', { seed: categoryData.seed })}` : ''}
            </p>
            {(categoryData.seeds || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-muted text-xs">{t('trends.categorySeedsLabel')}</span>
                {categoryData.seeds.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={loadingCategory}
                    onClick={() => fetchCategory(categoryData.category, s)}
                    className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer ${categoryData.seed === s ? 'border-accent text-accent' : 'border-edge text-muted bg-transparent'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              {['rising', 'top'].map((kind) => (
                <div key={kind} className="bg-surfaceAlt border border-edge rounded-xl p-4">
                  <p className="text-white text-sm font-semibold mb-1">{t(`trends.related_${kind}`)}</p>
                  <p className="text-muted text-[11px] mb-3">{t(`trends.related_${kind}Hint`)}</p>
                  {kind === 'rising' && categoryData.rising_filtered === false && (categoryData.rising || []).length > 0 && (
                    <p className="text-amber-500 text-[11px] mb-3">{t('trends.risingUnfiltered')}</p>
                  )}
                  {(categoryData[kind] || []).length === 0 ? (
                    <p className="text-muted text-xs">{t('trends.relatedEmpty')}</p>
                  ) : (
                    categoryData[kind].map((r) => (
                      <button
                        key={r.query}
                        type="button"
                        onClick={() => { searchRelated(r.query); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="w-full flex items-center justify-between gap-3 py-1.5 text-sm border-b border-edge last:border-0 bg-transparent cursor-pointer text-left"
                        title={t('trends.relatedClick')}
                      >
                        <span className="text-white truncate">{r.query}</span>
                        <span className={`whitespace-nowrap text-xs ${kind === 'rising' ? 'text-accent' : 'text-muted'}`}>
                          {r.breakout ? t('trends.breakout') : (kind === 'rising' ? r.value : r.extracted_value)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6 mb-6">
        {loadingLatest || loading ? (
          <p className="text-muted text-center py-12">{t('trends.checkingProducts')}</p>
        ) : trends.length === 0 && !hasFetched ? (
          <div className="text-center py-12">
            <p className="text-muted text-sm mb-1">{t('trends.emptyStateLine1')}</p>
            <p className="text-muted text-sm">{t('trends.emptyStateLine2')}</p>
          </div>
        ) : trends.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted text-sm mb-1">{t('trends.noneFoundLine1')}</p>
            <p className="text-muted text-sm">{t('trends.noneFoundLine2')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trends.map((trend) => {
              const isSelected = trend.product_id === selectedProductId;
              return (
                <div
                  key={trend.product_id}
                  onClick={() => { setSelectedProductId(trend.product_id); setSearchResult(null); }}
                  className={`bg-surfaceAlt border rounded-xl p-5 cursor-pointer transition-colors ${isSelected ? 'border-accent' : 'border-edge'}`}
                >
                  <p className="text-white text-sm font-semibold mb-3">{trend.product_name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-muted text-xs">{t('trends.volume')}: {trend.search_volume}</span>
                    <DirectionBadge direction={trend.trend_direction} t={t} />
                  </div>
                  <p className="text-muted text-[11px] mt-2">{t(`trends.period_${trend.period || '7d'}`)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {chartData && chartData.timeline && chartData.timeline.length > 0 && (
        <div className="bg-surface border border-edge rounded-2xl p-4 sm:p-6">
          <h3 className="text-white text-base font-semibold mb-4">{chartData.product_name}</h3>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData.timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#888" tick={{ fontSize: 11 }} />
              <YAxis stroke="#888" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 8 }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#c8f135' }}
              />
              <Line type="monotone" dataKey="value" name={t('trends.volume')} stroke="#c8f135" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default Trends;